import { useCallback } from 'react';

import { CLERK_ERROR_CODE } from '@/features/auth/shared/consts/clerk';
import type { AuthError } from '@/features/auth/shared/types/auth';
import { mapClerkError } from '@/features/auth/shared/utils/clerkErrorMapper';
import { extractClerkErrorCode } from '@/features/auth/shared/utils/extractClerkError';
import { useSignIn } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

export const useCredentialsLogin = (
  setError: (error: AuthError | null) => void,
  setLoading: (loading: boolean) => void
) => {
  const { isLoaded, signIn, setActive } = useSignIn();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const submitEmail = useCallback(
    async (email: string) => {
      if (!isLoaded) {
        return false;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await signIn.create({ identifier: email });

        if (result.status === 'complete' && result.createdSessionId) {
          await setActive({ session: result.createdSessionId });
          toast.success(t('success.auth.sessionExists'));
          void navigate('/');
          return true;
        }

        const hasPasswordFactor = result.supportedFirstFactors?.some((f) => f.strategy === 'password');
        if (!hasPasswordFactor) {
          setError({ type: 'no_password', message: t('auth.login.noPasswordSet') });
        }

        return true;
      } catch (err) {
        const errorCode = extractClerkErrorCode(err);

        if (
          errorCode === CLERK_ERROR_CODE.IDENTIFIER_ALREADY_SIGNED_IN ||
          errorCode === CLERK_ERROR_CODE.SESSION_EXISTS
        ) {
          toast.success(t('success.auth.sessionExists'));
          void navigate('/');
          return false;
        }

        setError(mapClerkError(err, t));
        return false;
      } finally {
        setLoading(false);
      }
    },
    [isLoaded, signIn, setActive, navigate, t, setError, setLoading]
  );

  const submitPassword = useCallback(
    async (email: string, password: string): Promise<void> => {
      if (!isLoaded) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result =
          signIn.status === 'needs_first_factor'
            ? await signIn.attemptFirstFactor({ strategy: 'password', password })
            : await signIn.create({ identifier: email, password });

        if (result.status === 'complete') {
          await setActive({ session: result.createdSessionId });
          void navigate('/');
          return;
        }

        setError({ type: 'password', message: t('error.login.unknown') });
      } catch (err) {
        setError(mapClerkError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [isLoaded, signIn, setActive, navigate, t, setError, setLoading]
  );

  return { submitEmail, submitPassword };
};
